import { Pipe, PipeTransform } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { ValidationError } from '../enums/ValidationError.enum';

// Important: Keep Pipes in the same file, since changing one requires changing both of them
@Pipe({
    name: 'controlErrorHandler',
    pure: true
})
export class ControlErrorHandlerPipe implements PipeTransform {
    private errorPriority = Object.values(ValidationError);

    public transform(errorKeys: ValidationErrors, touched: boolean): string | null {
        if (!errorKeys || !touched) {
            return null;
        }

        // Find the first error based on priority
        for (const errorType of this.errorPriority) {
            if (errorKeys[errorType]) {
                return this.getTranslationKey(errorType);
            }
        }

        // Fallback to first available error if none match priority
        const firstError = Object.keys(errorKeys)[0];

        // Backend errors carry the message string directly — no i18n lookup needed.
        if (firstError === 'backendError' && typeof errorKeys[firstError] === 'string') {
            return errorKeys[firstError] as string;
        }

        return this.getTranslationKey(firstError as ValidationError);
    }

    private getTranslationKey(errorType: ValidationError): string {
        return this.errorPriority.includes(errorType) ? `validation.${errorType}` : null;
    }
}

// Important: Keep Pipes in the same file, since changing one requires changing both of them
@Pipe({
    name: 'controlErrorParams',
    pure: true
})
export class ControlErrorParamsPipe implements PipeTransform {
    private errorPriority = Object.values(ValidationError);

    public transform(errorKeys: ValidationErrors, touched: boolean): Record<string, string | number> | null {
        if (!errorKeys || !touched) {
            return null;
        }

        // Find the first error based on priority
        for (const errorType of this.errorPriority) {
            if (errorKeys[errorType]) {
                return this.getTranslationParams(errorType, errorKeys[errorType]);
            }
        }

        // Fallback to first available error if none match priority
        const firstError = Object.keys(errorKeys)[0];
        if (errorKeys[firstError]) {
            return this.getTranslationParams(firstError as ValidationError, errorKeys[firstError]);
        }

        return null;
    }

    private getTranslationParams(
        errorType: ValidationError,
        errorValue: unknown
    ): Record<string, string | number> | null {
        switch (errorType) {
            case ValidationError.MIN_LENGTH: {
                const minLengthError = errorValue as { requiredLength: number; actualLength: number };
                return { requiredLength: minLengthError.requiredLength };
            }

            case ValidationError.MAX_LENGTH: {
                const maxLengthError = errorValue as { requiredLength: number; actualLength: number };
                return { maxLength: maxLengthError.requiredLength };
            }

            case ValidationError.MIN: {
                const minError = errorValue as { min: number; actual: number };
                return { min: minError.min };
            }

            case ValidationError.MAX: {
                const maxError = errorValue as { max: number; actual: number };
                return { max: maxError.max };
            }

            case ValidationError.INVALID_DATE: {
                const invalidDateError = errorValue as { value: string };
                return { value: invalidDateError.value };
            }

            case ValidationError.DATE_NOT_GREATER: {
                const dateNotGreaterError = errorValue as {
                    value: string;
                    compareValue: string | number;
                    actual: string;
                    expected: string;
                };
                return {
                    compareValue: dateNotGreaterError.compareValue,
                    actual: dateNotGreaterError.actual,
                    expected: dateNotGreaterError.expected
                };
            }

            case ValidationError.REQUIRED:
            case ValidationError.PATTERN:
            case ValidationError.EMAIL:
            case ValidationError.IMPORT_ERROR:
            case ValidationError.IMPORT_NAMES_ERROR:
            case ValidationError.END_DATE_BEFORE_START_DATE:
            case ValidationError.INVALID_OPTION:
            default:
                return null;
        }
    }
}
